import queryString from "query-string";
import { useEffect, useState } from "react";
import { Form, FormSpy } from "react-final-form";
import { Trans, useTranslation } from "react-i18next";
import i18next from "src/i18n/config";
import { confirmRegistration, updateProfile } from "src/services/accountService";
import AuthService, { buildAuthCodeEndpoint } from "src/services/authService";
import { getHomepageUrl, toAppUrl } from "src/services/configService";
import { getCountryData } from "src/services/countryService";
import { LocalStorage } from "src/services/localStorageService";
import { useDispatch } from "src/store";
import { setCurrentWorkspaceId } from "src/store/app";
import { useValidationSchema } from "src/ui/utils/useValidationSchema";

import { PageContainer } from "@dashboard/shared/components/PageContainer";
import { Box, Typography } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import ApmLink from "@shared/ApmLink";
import { PrimaryButton } from "@shared/CustomButton";
import { ControlledCheckboxField } from "@shared/form/CheckboxField";
import PasswordField from "@shared/form/PasswordField";
import { ControlledSelectField } from "@shared/form/SelectField/SelectField";
import { SelectOption } from "@shared/form/SelectField/SelectField.types";
import { TextField } from "@shared/form/TextField";
import { Logo } from "@shared/Logo/Logo";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

import { useStyles } from "./ConfirmRegistrationPage.style";
import { confirmRegistrationValidationSchema } from "./ConfirmRegistrationPage.validation";

type ConfirmationFlow = {
    email: string;
    codeVerifier: string;
    codeChallenge: string;
};

type Status = "pending" | "success" | "failed" | "data_stale";

type FormValues = API.Profile & {
    privacyChecked: boolean;
    eulaChecked: boolean;
    newsletterChecked: boolean;
    password: string;
    password_repeat: string;
};

const ConfirmRegistrationPage = () => {
    const [status, setStatus] = useState<Status | null>(null);
    const [email, setEmail] = useState("");
    const [countryList, setCountryList] = useState<SelectOption[]>([]);

    const { classes } = useStyles();
    const { t } = useTranslation(["pages"]);
    const dispatch = useDispatch();
    const validate = useValidationSchema(confirmRegistrationValidationSchema);

    const { id, token, code } = queryString.parse(window.location.search, {
        decode: true,
    });

    const onGoHome = () => {
        LocalStorage.clear();
        window.location.href = getHomepageUrl();
    };

    useEffect(() => {
        if (!code && id && token) {
            void (async () => {
                try {
                    const { email: _email, token: _token } = await confirmRegistration({
                        id: id as string,
                        token: token as string,
                    });
                    const codeVerifier = AuthService.helpers.generateRandomHexString(28);
                    const codeChallenge = await AuthService.helpers.generateCodeChallenge(codeVerifier);
                    LocalStorage.setItem("lastConfirmationFlow", { codeChallenge, codeVerifier, email: _email }, 180000);
                    const authEndpoint = await buildAuthCodeEndpoint({ codeChallenge, token: _token });
                    window.location.href = authEndpoint;
                } catch (_error) {
                    setStatus("failed");
                }
            })();
        } else if (code) {
            const { email: _email, codeVerifier } = LocalStorage.getItem<ConfirmationFlow>("lastConfirmationFlow") || {};

            if (!_email || !codeVerifier) {
                setStatus("data_stale");
                return;
            }

            void (async () => {
                try {
                    dispatch(setCurrentWorkspaceId(null));
                    await AuthService.initSessionViaAuthCode({
                        code: code as string,
                        codeVerifier,
                    });
                    setEmail(_email);
                    LocalStorage.removeItem("lastConfirmationFlow");
                    setStatus("success");
                } catch (err) {
                    showErrorToast(err);
                    setStatus("failed");
                }
            })();
        } else {
            setStatus("failed");
        }
    }, [code, dispatch, id, token]);

    useEffect(() => {
        void getCountryData().then((data) => setCountryList(data.countryList));
    }, []);

    const onSubmit = async (values: FormValues) => {
        try {
            const { success, message } =
                (await updateProfile({
                    ...values,
                    profileCompleted: true,
                })) ?? {};

            if (success) {
                showSuccessToast(t("pages:confirmRegistrationPage.toasts.welcomeMessage"));
                const session = AuthService.getSession();
                if (session && values.email && values.password) {
                    await AuthService.loadCookie(values.email, values.password, "", "");
                }
                window.location.href = toAppUrl("/dashboard");
            } else if (message) {
                if (Array.isArray(message)) {
                    const result = message.flatMap((msgObj: { message: string }) => msgObj.message);
                    if (result.length > 0) {
                        showErrorToast(result[0]);
                    }
                } else {
                    showErrorToast(message);
                }
            }
        } catch (err) {
            showErrorToast(err as string);
        }
    };

    if (status === "success") {
        return (
            <Box className={classes.root}>
                <Box component="header" className={classes.header}>
                    <Box className={classes.logoBox}>
                        <ApmLink href={getHomepageUrl()} className={classes.logoLink}>
                            <Logo variant="full" width={200} height={40} className={classes.logoImg} />
                        </ApmLink>
                    </Box>
                </Box>
                <Box className={classes.content}>
                    <Box className={classes.formWrapper}>
                        <Form<FormValues>
                            onSubmit={onSubmit}
                            validate={validate}
                            initialValues={{
                                email,
                            }}
                            render={({ handleSubmit }) => (
                                <form onSubmit={(e) => void handleSubmit(e)}>
                                    <Typography className={classes.sectionTitle}>
                                        {t("pages:confirmRegistrationPage.accountSettings")}
                                    </Typography>

                                    <Box className={classes.row}>
                                        <TextField
                                            name="email"
                                            label={t("pages:confirmRegistrationPage.emailPlaceholder")}
                                            disabled
                                            fullWidth
                                        />
                                    </Box>

                                    <Box className={classes.row}>
                                        <PasswordField
                                            name="password"
                                            label={t("pages:confirmRegistrationPage.passwordPlaceholder")}
                                            fullWidth
                                        />
                                    </Box>

                                    <Box className={classes.row}>
                                        <PasswordField
                                            name="password_repeat"
                                            label={t("pages:confirmRegistrationPage.passwordRepeatPlaceholder")}
                                            fullWidth
                                        />
                                    </Box>

                                    <Typography className={`${classes.sectionTitle} ${classes.sectionSpacing}`}>
                                        {t("pages:confirmRegistrationPage.personalInformation")}
                                    </Typography>

                                    <Box className={classes.row}>
                                        <TextField
                                            name="name"
                                            label={t("pages:confirmRegistrationPage.fullNamePlaceholder")}
                                            fullWidth
                                        />
                                    </Box>

                                    <Box className={classes.row}>
                                        <ControlledSelectField
                                            name="country"
                                            label={t("pages:confirmRegistrationPage.countryLabel")}
                                            fullWidth
                                            options={countryList}
                                            searchable
                                        />
                                    </Box>

                                    <Box className={classes.row}>
                                        <ControlledSelectField
                                            name="typeOfUsage"
                                            label={t("pages:confirmRegistrationPage.usageTypeLabel")}
                                            fullWidth
                                            options={[
                                                {
                                                    value: "nb",
                                                    label: t("pages:confirmRegistrationPage.usageTypeOptions.nonBusiness"),
                                                },
                                                { value: "b", label: t("confirmRegistrationPage.usageTypeOptions.business") },
                                            ]}
                                        />
                                    </Box>

                                    <FormSpy subscription={{ values: true }}>
                                        {({ values }) =>
                                            values?.typeOfUsage === "b" ? (
                                                <>
                                                    <Box className={classes.row}>
                                                        <TextField
                                                            name="companyName"
                                                            label={t("pages:confirmRegistrationPage.companyPlaceholder")}
                                                            fullWidth
                                                        />
                                                    </Box>
                                                    <Box className={classes.row}>
                                                        <TextField
                                                            name="companyVAT"
                                                            label={t("pages:confirmRegistrationPage.vatNumberPlaceholder")}
                                                            fullWidth
                                                        />
                                                    </Box>
                                                    <Box className={classes.row}>
                                                        <TextField
                                                            name="fieldOfApplication"
                                                            label={t("pages:confirmRegistrationPage.industryPlaceholder")}
                                                            fullWidth
                                                        />
                                                    </Box>
                                                </>
                                            ) : null
                                        }
                                    </FormSpy>

                                    <Typography className={`${classes.sectionTitle} ${classes.sectionSpacing}`}>
                                        {t("pages:confirmRegistrationPage.termsOfService")}
                                    </Typography>

                                    <Box className={classes.footer}>
                                        <Box className={classes.agreeBox}>
                                            <ControlledCheckboxField
                                                name="eulaChecked"
                                                label={
                                                    <Typography>
                                                        <Trans
                                                            ns="pages"
                                                            i18nKey="confirmRegistrationPage.eulaAgreement"
                                                            components={{
                                                                CustomLink: (
                                                                    <a
                                                                        href="https://emteria.com/legal/terms-of-use"
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                    />
                                                                ),
                                                            }}
                                                        />
                                                        <span className={classes.requiredLabel} />
                                                    </Typography>
                                                }
                                            />
                                        </Box>
                                        <Box className={classes.agreeBox}>
                                            <ControlledCheckboxField
                                                name="privacyChecked"
                                                label={
                                                    <Typography>
                                                        <Trans
                                                            ns="pages"
                                                            i18nKey="confirmRegistrationPage.privacyAgreement"
                                                            components={{
                                                                CustomLink: (
                                                                    <a
                                                                        href="https://emteria.com/legal/privacy-policy"
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                    />
                                                                ),
                                                            }}
                                                        />
                                                        <span className={classes.requiredLabel} />
                                                    </Typography>
                                                }
                                            />
                                        </Box>
                                        <Box className={classes.agreeBox}>
                                            <ControlledCheckboxField
                                                name="newsletterChecked"
                                                label={
                                                    <Typography>{t("confirmRegistrationPage.newsletterAgreement")}</Typography>
                                                }
                                            />
                                        </Box>
                                    </Box>
                                    <Box className={classes.confirmRegistrationContainer}>
                                        <PrimaryButton type="submit" fullWidth>
                                            {t("confirmRegistrationPage.confirmRegistrationButton")}
                                        </PrimaryButton>
                                    </Box>
                                </form>
                            )}
                        />
                    </Box>
                </Box>
            </Box>
        );
    }

    return (
        <PageContainer>
            {status === "pending" ? <Typography>{t("pages:confirmRegistrationPage.loading")}</Typography> : null}
            {status === "failed" ? (
                <Box className={classes.container}>
                    <Typography>{t("pages:confirmRegistrationPage.failed.title")}</Typography>
                    <Typography>{t("pages:confirmRegistrationPage.failed.description")}</Typography>
                    <Typography>{t("pages:confirmRegistrationPage.failed.instruction")}</Typography>
                    <PrimaryButton className={classes.button} onClick={onGoHome}>
                        {t("pages:confirmRegistrationPage.failed.button")}
                    </PrimaryButton>
                </Box>
            ) : null}
            {status === "data_stale" ? (
                <Box className={classes.container}>
                    <Typography className={classes.dataStaleText}>
                        {t("pages:confirmRegistrationPage.invalidPageState.description")}
                    </Typography>
                </Box>
            ) : null}
        </PageContainer>
    );
};

export default withMetaDescription(ConfirmRegistrationPage, {
    title: i18next.t("pages:confirmRegistrationPage.metaTitle"),
    description: i18next.t("pages:confirmRegistrationPage.metaDescription"),
});
